"use client";

import React, { useState, useEffect } from "react";
import {
  Box, Chip, CircularProgress, Dialog, DialogContent, Divider, Grid,
  IconButton, Paper, Table, TableBody, TableCell,
  TableHead, TableRow, Typography, FormControl, InputLabel, Select, MenuItem, Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import VerifiedIcon from "@mui/icons-material/Verified";
import EditIcon from "@mui/icons-material/Edit";
import { formatISTDateTime, maskPhone } from "../../helper";
import ChipToken, { getChipColor } from "../../components/ChipToken";
import { useSelector } from "react-redux";
import { axiosRequest } from "../../utility/axios";

const InfoRow = ({ label, value, valueColor }) => (
  <Box>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography fontWeight={600} color={valueColor || "text.primary"}>{value || "—"}</Typography>
  </Box>
);

const DenomTable = ({ title, denoms = {} }) => {
  const entries = Object.entries(denoms).filter(([, qty]) => Number(qty) > 0);
  if (!entries.length) return null;

  const total = entries.reduce((s, [d, q]) => s + Number(d) * Number(q), 0);

  return (
    <Box mb={2}>
      <Typography fontWeight={700} mb={1}>{title}</Typography>
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ background: "#f4f6f8" }}>
              <TableCell align="center" sx={{ fontWeight: 700 }}>Denomination</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700 }}>Quantity</TableCell>
              <TableCell align="right"  sx={{ fontWeight: 700 }}>Amount</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {entries.map(([denom, qty]) => {
              const cc = getChipColor(denom);
              return (
              <TableRow key={denom} hover sx={{ backgroundColor: cc.bg + "18" }}>
                <TableCell align="center"><ChipToken denomination={denom} size={40} /></TableCell>
                <TableCell align="center">{qty}</TableCell>
                <TableCell align="right">{(Number(denom) * Number(qty)).toLocaleString()}</TableCell>
              </TableRow>
              );})}
            <TableRow sx={{ background: "#f9f9f9" }}>
              <TableCell colSpan={2} align="right" sx={{ fontWeight: 700 }}>Total</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, color: "primary.main" }}>{total.toLocaleString()}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};

const ChipArrayTable = ({ title, chips = [] }) => {
  if (!chips.length) return null;
  const total = chips.reduce((s, c) => s + Number(c.amount || c.denomination * c.quantity), 0);
  return (
    <Box mb={2}>
      <Typography fontWeight={700} mb={1}>{title}</Typography>
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ background: "#f4f6f8" }}>
              <TableCell align="center" sx={{ fontWeight: 700 }}>Denomination</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700 }}>Quantity</TableCell>
              <TableCell align="right"  sx={{ fontWeight: 700 }}>Amount</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {chips.map((c) => {
              const cc = getChipColor(c.denomination);
              return (
              <TableRow key={c.denomination} hover sx={{ backgroundColor: cc.bg + "18" }}>
                <TableCell align="center"><ChipToken denomination={c.denomination} size={40} /></TableCell>
                <TableCell align="center">{c.quantity}</TableCell>
                <TableCell align="right">{Number(c.amount || c.denomination * c.quantity).toLocaleString()}</TableCell>
              </TableRow>
              );})}
            <TableRow sx={{ background: "#f9f9f9" }}>
              <TableCell colSpan={2} align="right" sx={{ fontWeight: 700 }}>Total</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, color: "primary.main" }}>{total.toLocaleString()}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};

const fmt = (v) => `${Number(v || 0).toLocaleString()}`;

const BalanceSnapshot = ({ before = {}, after = {} }) => {
  const rows = [
    { label: "Deposit Wallet",    key: "cash_deposite" },
    { label: "Winning Balance",   key: "winning_balance" },
    { label: "Promo Wallet",      key: "promotional_balance" },
    { label: "Loan Amount",       key: "loan_amount" },
    { label: "Loan Distributed",  key: "loan_distributed" },
  ];
  const hasData = rows.some(
    ({ key }) => before[key] !== undefined || after[key] !== undefined,
  );
  if (!hasData) return null;

  return (
    <Box mb={2}>
      <Typography fontWeight={700} mb={1}>Wallet Balance Snapshot</Typography>
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ background: "#f4f6f8" }}>
              <TableCell sx={{ fontWeight: 700 }}>Wallet</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, color: "text.secondary" }}>Before</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, color: "text.secondary" }}>After</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Change</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(({ label, key }) => {
              const b = Number(before[key] || 0);
              const a = Number(after[key]  || 0);
              const diff = a - b;
              const diffColor = diff > 0 ? "#388e3c" : diff < 0 ? "#d32f2f" : "text.secondary";
              const diffLabel = diff === 0 ? "—" : `${diff > 0 ? "+" : ""}${Math.abs(diff).toLocaleString()}`;
              return (
                <TableRow key={key} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{label}</TableCell>
                  <TableCell align="right" sx={{ color: "text.secondary" }}>{fmt(b)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>{fmt(a)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: diffColor }}>{diffLabel}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};

const TXN_TYPE_COLOR = {
  DEPOSIT:       "success",
  WITHDRAW:      "error",
  CHIP_ISSUE:    "info",
  CHIP_CASHOUT:  "warning",
  RAKE:          "secondary",
  TIP:           "secondary",
  LOAN_DISBURSE: "secondary",
  LOAN_REPAY:    "default",
  CHIP_EXCHANGE: "default",
};

// Derives counter state BEFORE the transaction from the stored AFTER snapshot.
const deriveBeforeSnapshot = (snap, data) => {
  const { txn_type, payment_mode, txn_amount = 0, details = {} } = data
  const amt = Number(txn_amount || 0)

  let cash_before  = Number(snap.available_cash  || 0)
  let bank_before  = Number(snap.available_bank  || 0)
  let chips_before = Number(snap.available_chips || 0)
  const denoms_after  = snap.chip_denominations || {}
  const denoms_before = { ...denoms_after }

  // Cash effects
  if (txn_type === "DEPOSIT"  && payment_mode === "CASH") cash_before -= amt
  if (txn_type === "WITHDRAW" && payment_mode === "CASH") cash_before += amt
  if (txn_type === "EXPENSE")                             cash_before += amt

  // Bank effects
  if (txn_type === "DEPOSIT"  && payment_mode === "BANK") bank_before -= amt
  if (txn_type === "WITHDRAW" && payment_mode === "BANK") bank_before += amt

  // Chip effects — reverse issue/cashout/rake by denomination
  const applyDenoms = (chipsArray, sign) => {
    if (!Array.isArray(chipsArray)) return
    chipsArray.forEach(c => {
      const d = String(c.denomination)
      const q = Number(c.quantity || 0)
      denoms_before[d] = Math.max(0, (Number(denoms_before[d]) || 0) + sign * q)
      chips_before     += sign * Number(c.denomination) * q
    })
  }

  if (txn_type === "CHIP_ISSUE") {
    applyDenoms(details.chips, +1)  // chips were removed; add back for "before"
  } else if (txn_type === "CHIP_CASHOUT" || txn_type === "RAKE" || txn_type === "TIP") {
    applyDenoms(details.chips, -1)  // chips were added; subtract for "before"
  } else if (txn_type === "CHIP_EXCHANGE") {
    applyDenoms(details.chips_received, -1) // received were added
    applyDenoms(details.chips_given,    +1) // given were removed
  }

  return {
    cash:               Math.max(0, Math.round(cash_before)),
    bank:               Math.round(bank_before),
    chips:              Math.max(0, Math.round(chips_before)),
    chip_denominations: denoms_before,
  }
}

const CounterBox = ({ label, cashColor, bankColor, chipsColor, cash, bank, chips, denominations }) => (
  <Box>
    <Typography variant="caption" sx={{ opacity: 0.7, mb: 0.5, display: "block", textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</Typography>
    <Box sx={{ display: "flex", gap: 1.5, mb: 1, flexWrap: "wrap" }}>
      <Box sx={{ background: "rgba(255,255,255,0.1)", borderRadius: 2, p: 1.2, textAlign: "center", minWidth: 90 }}>
        <Typography variant="caption" sx={{ opacity: 0.7 }}>Cash</Typography>
        <Typography variant="h6" fontWeight={800} sx={{ color: cashColor }}>
          {Number(cash || 0).toLocaleString("en-IN")}
        </Typography>
      </Box>
      <Box sx={{ background: "rgba(255,255,255,0.1)", borderRadius: 2, p: 1.2, textAlign: "center", minWidth: 90 }}>
        <Typography variant="caption" sx={{ opacity: 0.7 }}>Bank</Typography>
        <Typography variant="h6" fontWeight={800} sx={{ color: bankColor }}>
          {Number(bank || 0).toLocaleString("en-IN")}
        </Typography>
      </Box>
      <Box sx={{ background: "rgba(255,255,255,0.1)", borderRadius: 2, p: 1.2, textAlign: "center", minWidth: 90 }}>
        <Typography variant="caption" sx={{ opacity: 0.7 }}>Chips</Typography>
        <Typography variant="h6" fontWeight={800} sx={{ color: chipsColor }}>
          {Number(chips || 0).toLocaleString("en-IN")}
        </Typography>
      </Box>
    </Box>
    {denominations && Object.keys(denominations).length > 0 && (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8 }}>
        {Object.entries(denominations)
          .filter(([, q]) => Number(q) > 0)
          .sort((a, b) => Number(a[0]) - Number(b[0]))
          .map(([d, q]) => {
            const cc = getChipColor(d)
            return (
              <Box key={d} sx={{ px: 1.2, py: 0.6, borderRadius: 1.5, background: cc.bg, textAlign: "center", minWidth: 62 }}>
                <Typography variant="caption" sx={{ color: cc.text, opacity: 0.85, display: "block" }}>{d}</Typography>
                <Typography variant="body2" fontWeight={700} sx={{ color: cc.text }}>{q} pcs</Typography>
              </Box>
            )
          })}
      </Box>
    )}
  </Box>
)

const CounterBeforeAfter = ({ snapshot, data }) => {
  const before = deriveBeforeSnapshot(snapshot, data)
  const after  = snapshot

  const cashDiff  = Number(after.available_cash  || 0) - before.cash
  const bankDiff  = Number(after.available_bank  || 0) - before.bank
  const chipsDiff = Number(after.available_chips || 0) - before.chips
  const fmtDiff   = (v) => v === 0 ? "—" : `${v > 0 ? "+" : ""}${Math.abs(v).toLocaleString("en-IN")}`
  const diffColor = (v) => v > 0 ? "#69f0ae" : v < 0 ? "#ff5252" : "rgba(255,255,255,0.5)"

  return (
    <Paper sx={{ p: 2, borderRadius: 2, background: "linear-gradient(135deg,#0f2027,#203a43,#2c5364)", color: "#fff" }}>
      <Grid container spacing={2} alignItems="flex-start">
        <Grid item xs={12} sm={5}>
          <CounterBox
            label="Before"
            cashColor="rgba(255,255,255,0.7)"
            bankColor="rgba(255,255,255,0.7)"
            chipsColor="rgba(255,255,255,0.7)"
            cash={before.cash}
            bank={before.bank}
            chips={before.chips}
            denominations={before.chip_denominations}
          />
        </Grid>

        {/* Arrow + diff */}
        <Grid item xs={12} sm={2} sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", pt: { sm: 3 } }}>
          <Typography sx={{ fontSize: 28, opacity: 0.6 }}>→</Typography>
          <Box sx={{ textAlign: "center", mt: 0.5 }}>
            <Typography variant="caption" sx={{ opacity: 0.6, display: "block" }}>Cash</Typography>
            <Typography variant="body2" fontWeight={700} sx={{ color: diffColor(cashDiff) }}>{fmtDiff(cashDiff)}</Typography>
            <Typography variant="caption" sx={{ opacity: 0.6, display: "block", mt: 0.5 }}>Bank</Typography>
            <Typography variant="body2" fontWeight={700} sx={{ color: diffColor(bankDiff) }}>{fmtDiff(bankDiff)}</Typography>
            <Typography variant="caption" sx={{ opacity: 0.6, display: "block", mt: 0.5 }}>Chips</Typography>
            <Typography variant="body2" fontWeight={700} sx={{ color: diffColor(chipsDiff) }}>{fmtDiff(chipsDiff)}</Typography>
          </Box>
        </Grid>

        <Grid item xs={12} sm={5}>
          <CounterBox
            label="After"
            cashColor="#69f0ae"
            bankColor="#ffcc80"
            chipsColor="#40c4ff"
            cash={after.available_cash}
            bank={after.available_bank}
            chips={after.available_chips}
            denominations={after.chip_denominations}
          />
        </Grid>
      </Grid>
    </Paper>
  )
}

export default function ViewTranscation({ open, handleClose, data, onUpdate }) {
  const token = useSelector((s) => s.loginSlice.token);

  const [snapshot, setSnapshot]         = useState(null);
  const [snapshotLoading, setSnapshotLoading] = useState(false);

  // account editing
  const [accounts, setAccounts]           = useState([]);
  const [editingAccount, setEditingAccount] = useState(false);
  const [accountId, setAccountId]         = useState("");
  const [accountSaving, setAccountSaving] = useState(false);
  const [localDetails, setLocalDetails]   = useState({});

  useEffect(() => {
    if (open) {
      setLocalDetails(data?.details || {});
      setEditingAccount(false);
      setAccountId(data?.details?.paid_account_id || "");
      if (!accounts.length) {
        axiosRequest("GET", `/accounts`, token).then((r) => setAccounts(r?.data?.data || [])).catch(() => {});
      }
      // For DEPOSIT: if account not stored in details, try to backfill from the AccountLedger
      if (data?.txn_type === "DEPOSIT" && !data?.details?.paid_account_id && data?._id) {
        axiosRequest("GET", `/account-ledger?transaction_ref=${data._id}`, token)
          .then((r) => {
            const entry = r?.data?.data?.list?.[0];
            if (entry?.account) {
              const acc = entry.account;
              setLocalDetails((prev) => ({
                ...prev,
                paid_account_id:    acc._id || "",
                paid_account_name:  acc.name || "",
                paid_account_phone: acc.phone || "",
              }));
              setAccountId(acc._id || "");
            }
          })
          .catch(() => {});
      }
    }
  }, [open, data?._id]);

  const handleAccountSave = async () => {
    setAccountSaving(true);
    try {
      const res = await axiosRequest("PATCH", `/transaction/${data._id}`, token, { account_id: accountId });
      if (res?.status === "success") {
        const found = accounts.find((a) => String(a._id) === String(accountId));
        setLocalDetails((prev) => {
          if (!accountId) {
            // User explicitly cleared the account
            return { ...prev, paid_account_id: undefined, paid_account_name: undefined, paid_account_phone: undefined };
          }
          return {
            ...prev,
            paid_account_id:    accountId,
            paid_account_name:  found?.name  || prev.paid_account_name,
            paid_account_phone: found?.phone !== undefined ? found.phone : prev.paid_account_phone,
          };
        });
        setEditingAccount(false);
        if (onUpdate) onUpdate();
      }
    } finally {
      setAccountSaving(false);
    }
  };

  useEffect(() => {
    if (!open || !data?._id) return;

    // Use stored snapshot when available (new transactions) — skip API call
    const stored = data.counter_snapshot
    if (stored?.cash != null) {
      setSnapshot({ available_cash: stored.cash, available_bank: stored.bank ?? 0, available_chips: stored.chips, chip_denominations: stored.chip_denominations || {} })
      setSnapshotLoading(false)
      return
    }

    // Older transactions: compute on demand via API
    setSnapshot(null);
    setSnapshotLoading(true);
    axiosRequest("GET", `/transaction/${data._id}/counter-snapshot`, token)
      .then((res) => { if (res?.status === "success") setSnapshot(res.data); })
      .finally(() => setSnapshotLoading(false));
  }, [open, data?._id]);

  if (!data || !data._id) return null;

  const { user_id, txn_by, offer_id, table_id } = data;
  const details = localDetails;

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: 3 } }}>

      {/* HEADER */}
      <Box sx={{ background: "linear-gradient(135deg,#1e3c72,#2a5298)", px: 3, py: 2, color: "#fff", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Box>
          <Typography variant="h6" fontWeight={700}>Transaction Details</Typography>
          <Typography variant="caption" sx={{ opacity: 0.8 }}>{data.local_txn_id}</Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          <Chip
            label={data.txn_type}
            color={TXN_TYPE_COLOR[data.txn_type] || "default"}
            size="small"
          />
          <IconButton onClick={handleClose} sx={{ color: "#fff" }}><CloseIcon /></IconButton>
        </Box>
      </Box>

      <DialogContent sx={{ p: 3 }}>

        {/* SUMMARY STRIP */}
        <Paper sx={{ p: 2, mb: 3, borderRadius: 2, background: "#f8f9fc", border: "1px solid #e8eaf6" }}>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <InfoRow label="Transaction Amount" value={`${Number(data.txn_amount || 0).toLocaleString()}`} valueColor="primary.main" />
            </Grid>
            {data.cashback_amount > 0 && (
              <Grid item xs={6} sm={3}>
                <InfoRow label="Cashback (Promo)" value={`${Number(data.cashback_amount).toLocaleString()}`} valueColor="secondary.main" />
              </Grid>
            )}
            <Grid item xs={6} sm={3}>
              <InfoRow label="Payment Mode" value={data.payment_mode} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <InfoRow label="Date & Time" value={formatISTDateTime(data.txn_date)} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <InfoRow label="Category" value={data.depositCategory} />
            </Grid>
            {details.utr && (
              <Grid item xs={6} sm={3}>
                <InfoRow label="UTR Number" value={details.utr} />
              </Grid>
            )}
            {(details.remarks || details.remark) && (
              <Grid item xs={12}>
                <InfoRow label="Remark / Comment" value={details.remarks || details.remark} valueColor="#7b1fa2" />
              </Grid>
            )}
            {details.paid_remark && (
              <Grid item xs={12}>
                <InfoRow label="Paid Remark" value={details.paid_remark} valueColor="#1565c0" />
              </Grid>
            )}
            {(["WITHDRAW", "EXPENSE"].includes(data.txn_type) && data.payment_status === "SUCCESS" || data.txn_type === "DEPOSIT") && (
              <Grid item xs={12}>
                {editingAccount ? (
                  <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                    <FormControl size="small" sx={{ minWidth: 220 }}>
                      <InputLabel>Paid to Account</InputLabel>
                      <Select value={accountId} label="Paid to Account" onChange={(e) => setAccountId(e.target.value)}>
                        <MenuItem value="">— None —</MenuItem>
                        {accounts.map((a) => (
                          <MenuItem key={a._id} value={a._id}>{a.name}{a.phone ? ` — ${a.phone}` : ""}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Button size="small" variant="contained" color="success" onClick={handleAccountSave} disabled={accountSaving}>
                      {accountSaving ? "Saving…" : "Save"}
                    </Button>
                    <Button size="small" variant="outlined" onClick={() => { setEditingAccount(false); setAccountId(details.paid_account_id || ""); }}>
                      Cancel
                    </Button>
                  </Box>
                ) : (
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Paid to Account</Typography>
                      <Typography fontWeight={600} color={details.paid_account_name ? "#1b5e20" : "text.disabled"}>
                        {details.paid_account_name
                          ? details.paid_account_name + (details.paid_account_phone ? ` — ${details.paid_account_phone}` : "")
                          : "—"}
                      </Typography>
                    </Box>
                    <IconButton size="small" onClick={() => setEditingAccount(true)} sx={{ ml: 0.5 }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </Grid>
            )}
            {data.is_verified && (
              <Grid item xs={12}>
                <Box display="flex" alignItems="center" gap={1} sx={{ mt: 0.5 }}>
                  <VerifiedIcon sx={{ color: "#2e7d32", fontSize: 18 }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">Verified By</Typography>
                    <Typography fontWeight={600} color="success.main">
                      {data.verified_by?.name || "Admin"}
                      {data.verified_at ? ` — ${formatISTDateTime(data.verified_at)}` : ""}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            )}
          </Grid>
        </Paper>

        <Grid container spacing={2} mb={2}>

          {/* PLAYER */}
          <Grid item xs={12} sm={6}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Typography fontWeight={700} mb={1}>Player</Typography>
              <InfoRow label="Name"      value={user_id?.name} />
              <Box mt={1}><InfoRow label="Phone"     value={maskPhone(user_id?.phone)} /></Box>
              <Box mt={1}><InfoRow label="Player ID" value={user_id?.playerId} /></Box>
            </Paper>
          </Grid>

          {/* CASHIER */}
          <Grid item xs={12} sm={6}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Typography fontWeight={700} mb={1}>Processed By</Typography>
              <InfoRow label="Cashier Name"  value={txn_by?.name} />
              <Box mt={1}><InfoRow label="Cashier Email" value={txn_by?.email} /></Box>
            </Paper>
          </Grid>

          {/* OFFER (if applied) */}
          {offer_id && (
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: "secondary.light", background: "#fdf4ff" }}>
                <Typography fontWeight={700} mb={1} color="secondary.main">Offer Applied</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}><InfoRow label="Coupon Code"   value={offer_id?.coupon_code} /></Grid>
                  <Grid item xs={6} sm={3}><InfoRow label="Type"          value={offer_id?.coupon_type} /></Grid>
                  <Grid item xs={6} sm={3}><InfoRow label="Cashback %"    value={offer_id?.max_cashback_percent > 0 ? `${offer_id.max_cashback_percent}%` : "—"} /></Grid>
                  <Grid item xs={6} sm={3}><InfoRow label="Cashback Added" value={`${Number(data.cashback_amount || 0).toLocaleString()}`} valueColor="secondary.main" /></Grid>
                  {table_id && (
                    <Grid item xs={6} sm={3}><InfoRow label="Table" value={table_id?.tableName || "—"} /></Grid>
                  )}
                </Grid>
              </Paper>
            </Grid>
          )}

        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* DENOMINATION TABLES */}
        <DenomTable title="Chip Denominations"  denoms={details.chipDenominations} />

        {/* CHIP ISSUE — chips array */}
        {Array.isArray(details.chips) && details.chips.length > 0 && (
          <Box mb={2}>
            <Typography fontWeight={700} mb={1}>Chips Issued</Typography>
            <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ background: "#f4f6f8" }}>
                    <TableCell align="center" sx={{ fontWeight: 700 }}>Denomination</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700 }}>Quantity</TableCell>
                    <TableCell align="right"  sx={{ fontWeight: 700 }}>Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {details.chips.map((c) => (
                    <TableRow key={c.denomination} hover>
                      <TableCell align="center">{Number(c.denomination).toLocaleString()}</TableCell>
                      <TableCell align="center">{c.quantity}</TableCell>
                      <TableCell align="right">{Number(c.amount || c.denomination * c.quantity).toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ background: "#f9f9f9" }}>
                    <TableCell colSpan={2} align="right" sx={{ fontWeight: 700 }}>Total</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: "primary.main" }}>
                      {details.chips.reduce((s, c) => s + Number(c.amount || c.denomination * c.quantity), 0).toLocaleString()}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Paper>
          </Box>
        )}

        {/* CHIP ISSUE — deduction breakdown */}
        {details.deduction_breakdown && (
          <Box mb={2}>
            <Typography fontWeight={700} mb={1}>Wallet Deduction</Typography>
            <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
              <Table size="small">
                <TableBody>
                  {[
                    { label: "Promo Wallet",   key: "from_promo",   color: "#9c27b0" },
                    { label: "Deposit Wallet", key: "from_deposit", color: "#1976d2" },
                    { label: "Winning Wallet", key: "from_winning", color: "#388e3c" },
                    { label: "Loan Wallet",    key: "from_loan",    color: "#f59e0b" },
                  ].filter(({ key }) => Number(details.deduction_breakdown[key]) > 0)
                   .map(({ label, key, color }) => (
                    <TableRow key={key} hover>
                      <TableCell sx={{ fontWeight: 600, color }}>{label}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color }}>
                        − {Number(details.deduction_breakdown[key]).toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          </Box>
        )}

        {/* CHIP EXCHANGE — chips given / received */}
        {Array.isArray(details.chips_given) && details.chips_given.length > 0 && (
          <ChipArrayTable title="Chips Given (Returned to Stock)" chips={details.chips_given} />
        )}
        {Array.isArray(details.chips_received) && details.chips_received.length > 0 && (
          <ChipArrayTable title="Chips Received (From Stock)" chips={details.chips_received} />
        )}

        {/* BALANCE BEFORE / AFTER — all transaction types */}
        <BalanceSnapshot before={data.balance_before} after={data.balance_after} />

        {/* COUNTER SNAPSHOT — BEFORE & AFTER */}
        <Divider sx={{ my: 2 }} />
        <Typography fontWeight={700} mb={1}>Counter State — Before &amp; After Transaction</Typography>
        {snapshotLoading ? (
          <Box display="flex" alignItems="center" gap={1} py={1}>
            <CircularProgress size={18} /><Typography variant="body2" color="text.secondary">Loading counter state…</Typography>
          </Box>
        ) : snapshot ? (
          <CounterBeforeAfter snapshot={snapshot} data={data} />
        ) : (
          <Typography variant="body2" color="text.secondary">No shift data available for this transaction.</Typography>
        )}

        {/* CHIP CASHOUT — credit breakdown */}
        {details.credit_breakdown && (
          <Box mb={2}>
            <Typography fontWeight={700} mb={1}>Credit Breakdown</Typography>
            <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
              <Table size="small">
                <TableBody>
                  {[
                    { label: "Loan Repaid",      key: "loan_repay", color: "#f59e0b" },
                    { label: "Added to Winning", key: "to_winning", color: "#388e3c" },
                  ].filter(({ key }) => Number(details.credit_breakdown[key]) > 0)
                   .map(({ label, key, color }) => (
                    <TableRow key={key} hover>
                      <TableCell sx={{ fontWeight: 600, color }}>{label}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color }}>
                        + {Number(details.credit_breakdown[key]).toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          </Box>
        )}

      </DialogContent>
    </Dialog>
  );
}
